import { useController, type FieldValues } from 'react-hook-form';
import { Form, Radio, type RadioProps } from 'semantic-ui-react';

import type { FieldProps } from './field.types';

export type FieldRadioProps<T extends FieldValues = FieldValues> = FieldProps<
  T,
  RadioProps,
  'checked' | 'label'
>;

/**
 * Radio button wired to React Hook Form via `useController`.
 *
 * Renders a single option — use multiple `Field.Radio` components with the
 * same `name` to form a group. The selected option is determined by comparing
 * the field's current value against each radio's `value` prop.
 *
 * Error display should be handled at the group level (e.g. with
 * `FieldError`) since individual radios don't own the field's validity.
 *
 * All additional props are forwarded to the underlying SUI `Radio`.
 */
export const FieldRadio = <T extends FieldValues>({
  className,
  control,
  label,
  name,
  required,
  value,
  width,
  ...rest
}: FieldRadioProps<T>) => {
  const { field, fieldState } = useController({ control, name });

  return (
    <Form.Field
      className={className}
      error={!!fieldState.error}
      required={required}
      width={width}
    >
      <Radio
        {...rest}
        ref={field.ref}
        checked={field.value === value}
        label={label}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(_, { value: radioValue }) => field.onChange(radioValue)}
        value={value}
      />
    </Form.Field>
  );
};
